import { useMemo } from 'react';
import { type ImportStructure, fieldsFor } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { Select } from '#client/components/ui/Select';
import { UNMAPPED, buildColumnOptions, markUsedColumns, remapField } from '../mapping.logic';
import type { ImportRunState } from '../useImportRun';

/** What each column of the sheet means: one select per field of the entity. */
export function MapColumnsCard({
  structure,
  isBusy,
  onChange,
}: {
  structure: ImportStructure;
  isBusy: boolean;
  onChange: ImportRunState['updateMapping'];
}) {
  const { run } = structure;
  const fields = fieldsFor(run.entity);

  const columnOptions = useMemo(
    () => buildColumnOptions(structure.headers, 'Not in this sheet'),
    [structure.headers],
  );

  const missingRequired = fields.filter((field) => field.required && !(field.key in run.mapping));

  return (
    <Card
      title="Columns"
      subtitle="What each column of the sheet means"
      actions={
        <Button
          size="sm"
          onClick={() => void onChange({ mapping: {} })}
          disabled={isBusy}
          // Clearing to nothing and letting the server re-detect is more
          // useful than a stored "original" — the detection depends on the
          // header row, which the operator may since have moved.
        >
          Guess again
        </Button>
      }
    >
      {missingRequired.length > 0 && (
        <p className="c-import-missing">
          Still needed: {missingRequired.map((field) => field.label).join(', ')}
        </p>
      )}

      <div className="c-form-grid">
        {fields.map((field) => {
          const column = run.mapping[field.key];
          return (
            <Select
              key={field.key}
              label={field.label}
              required={field.required}
              options={markUsedColumns(columnOptions, run.mapping, field.key)}
              value={column === undefined ? UNMAPPED : String(column)}
              onChange={(choice) =>
                void onChange({ mapping: remapField(run.mapping, field.key, choice) })
              }
              disabled={isBusy}
              helpText={field.help}
            />
          );
        })}
      </div>
    </Card>
  );
}
